import React, { useEffect, useState } from 'react';
import { ArrowRight, Star, Shield, Clock, MapPin } from 'lucide-react';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const trustIndicators = [
    { icon: Star, text: "4.9/5 étoiles", subtext: "500+ avis clients" },
    { icon: Shield, text: "Garanti", subtext: "Pièces premium" },
    { icon: Clock, text: "Rapide", subtext: "Service 30min" },
    { icon: MapPin, text: "Mobile", subtext: "Partout à Casa" }
  ];

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url('/images/IMG_006200.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      itemScope
      itemType="https://schema.org/AutoRepair"
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40"></div>
      
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400/20 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
          }}
        />
        <div 
          className="absolute top-3/4 right-1/4 w-96 h-96 bg-indigo-400/20 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
          style={{
            transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`,
            animationDelay: '1s'
          }}
        />
        <div 
          className="absolute bottom-1/4 left-1/2 w-96 h-96 bg-cyan-400/20 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            animationDelay: '2s'
          }}
        />
        
        {/* Floating Elements */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 bg-blue-400/30 rounded-full animate-bounce`}
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 2) * 40}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i * 0.5}s`
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Main Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
              Service automobile mobile #1 à Casablanca
            </div>

            {/* Main Headline */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg"
              itemProp="name"
            >
              Votre entretien auto,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                où vous voulez
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                quand vous voulez
              </span>
            </h1>

            {/* Subtitle */}
            <p 
              className="text-xl text-gray-100 mb-8 leading-relaxed drop-shadow-md"
              itemProp="description"
            >
              Vidange mobile, mécanique et carrosserie par des professionnels de confiance, 
              partout à Casablanca. Service rapide, qualité garantie.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="https://reservation.faucon-bleu.ma/reservation"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                <span>Réserver maintenant</span>
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              
              <a
                href="tel:+212702094593"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-blue-400 text-blue-400 font-semibold rounded-xl hover:bg-blue-400 hover:text-white transition-all duration-300"
              >
                Appeler maintenant
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {trustIndicators.map((indicator, index) => {
                const Icon = indicator.icon;
                return (
                  <div 
                    key={index}
                    className={`text-center p-4 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${(index + 2) * 200}ms` }}
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm text-white rounded-lg mb-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-sm font-semibold text-white">{indicator.text}</div>
                    <div className="text-xs text-gray-200">{indicator.subtext}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual Content */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            
            {/* Main Partnership Visual */}
            <div className="relative">
              {/* Background Card */}
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
                
                {/* Partnership Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl lg:text-3xl font-bold text-blue-600 mb-4">
                    En partenariat officiel avec les huiles moteur Shell Helix
                  </h3>
                  
                  {/* Shell Helix Logo */}
                  <div className="relative inline-block">
                    <img
                      src="/images/oil_helix.png"
                      alt="Shell Helix Ultra - Huiles moteur premium"
                      className="h-20 lg:h-32 w-auto mx-auto"
                      loading="eager"
                    />
                    
                    {/* Verification Badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <div className="text-2xl mb-2">🛠️</div>
                    <div className="text-sm font-semibold text-gray-800">Service Mobile</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl mb-2">⚡</div>
                    <div className="text-sm font-semibold text-gray-800">Rapide</div>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <div className="text-2xl mb-2">🏆</div>
                    <div className="text-sm font-semibold text-gray-800">Qualité Premium</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <div className="text-2xl mb-2">📱</div>
                    <div className="text-sm font-semibold text-gray-800">Réservation Easy</div>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -top-[50px] -left-4 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border animate-float">
                <div className="text-2xl font-bold text-green-600">500+</div>
                <div className="text-sm text-gray-600">Clients satisfaits</div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border animate-float-delayed">
                <div className="text-2xl font-bold text-blue-600">30min</div>
                <div className="text-sm text-gray-600">Temps moyen</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" className="w-full h-auto text-white">
          <path fill="currentColor" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,69.3C960,85,1056,107,1152,112C1248,117,1344,107,1392,101.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
        </svg>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Faucon Bleu",
            "description": "Service d'entretien automobile mobile à Casablanca - Vidange, mécanique et carrosserie à domicile",
            "url": "https://faucon-bleu.ma",
            "telephone": "+212702094593",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Casablanca",
              "addressCountry": "MA"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 33.428176,
              "longitude": -7.6433821
            },
            "serviceArea": {
              "@type": "City",
              "name": "Casablanca"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services automobiles",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Vidange mobile",
                    "description": "Service de vidange à domicile"
                  }
                },
                {
                  "@type": "Offer", 
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Mécanique automobile",
                    "description": "Réparation et entretien mécanique"
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;